import type { ApiResult } from '@bldsoft/cappie/rest'
import type { HttpError, HttpOk } from '@bldsoft/cappie/transport/http'
import type { HttpResponseInfo, RequestError } from '@setplex/tria-api'

import {
  RequestCancelledError,
  RequestFailedError,
  RequestHttpError,
  RequestTimeoutError,
} from '@setplex/tria-api'

/**
 * Normalize transport response into a shape any consumer can read
 */
export function toResponseInfo(http: HttpOk | HttpError): HttpResponseInfo {
  return {
    ok: http.kind === 'success',
    status: http.status,
    statusText: http.statusText,
    url: http.url,
    headers: http.headers,
  }
}

/**
 * Map an API result into a request error, if it is one
 */
export function toRequestError(
  result: ApiResult<unknown, unknown>
): RequestError | undefined {
  switch (result.kind) {
    case 'success': {
      return
    }

    // a business error on a successful response is not an HTTP failure
    case 'api-error': {
      const response = toResponseInfo(result.http)
      return response.ok ? undefined : new RequestHttpError(response)
    }

    case 'http-error': {
      return new RequestHttpError(toResponseInfo(result.http))
    }

    case 'timeout': {
      return new RequestTimeoutError(new Error(result.message))
    }

    // request policy refusal / rest-barrier drop is a cancelled request
    case 'abort':
    case 'block':
    case 'drop': {
      return new RequestCancelledError(new Error(result.message))
    }

    case 'validation': {
      const issues = result.issues.map((issue) => issue.message).join(', ')
      return new RequestFailedError(
        new Error(`Invalid ${result.phase}: ${issues}`)
      )
    }

    default: {
      return new RequestFailedError(new Error(result.message))
    }
  }
}

/**
 * Reject with `{ error, payload }`, the failure contract of every API effect
 */
function fail(result: ApiResult<unknown, unknown>): never {
  throw {
    error: toRequestError(result),
    payload: 'body' in result ? result.body : undefined,
  }
}

/**
 * Unwrapped `payload` of a successful response.
 * Endpoints without an envelope resolve to the whole body.
 */
export function payload<T>(result: ApiResult<unknown, unknown>): T {
  if (result.kind !== 'success') fail(result)
  return result.payload as T
}

/**
 * Whole body of a successful response, envelope included
 */
export function body<T>(result: ApiResult<unknown, unknown>): T {
  if (result.kind !== 'success') fail(result)
  return result.body as T
}

/**
 * Unwrapped `payload` of a successful response, with response information,
 * for the rare cases when a status code carries meaning
 */
export function raw<T>(result: ApiResult<unknown, unknown>): {
  response: HttpResponseInfo
  payload: T
} {
  if (result.kind !== 'success') fail(result)
  return {
    response: toResponseInfo(result.http),
    payload: result.payload as T,
  }
}

/**
 * Check if error is an AbortError, which means request was cancelled by client
 */
export function isAbortError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  if ('name' in error && error.name === 'AbortError') return true
  if (error instanceof RequestCancelledError) return true
  return 'error' in error && isAbortError(error.error)
}
